import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { DateFormatService, DATE_FORMAT_OPTIONS } from '../../../core/services/date-format.service';

interface PlanRow {
  months: number | null;
  label: string;
  subscriptionType: string;
  charges: number | null;
}

const TYPE_MAP: Record<number, string> = {
  1: 'Monthly', 3: 'Quarterly', 6: 'Half-Yearly', 12: 'Annual', 24: 'Bi-Annual'
};

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit {
  plans: PlanRow[] = [];
  isLoading = false;
  isSaving = false;
  errorMessage = '';
  successMessage = '';

  // ── Label name source ──
  labelNameSource: 'center' | 'licensee' | 'custom' = 'center';
  labelNameCustomText = '';
  isSavingNameSource = false;
  nameSourceSuccess  = '';
  nameSourceError    = '';

  // ── General settings ──
  depositAmount: number = 0;
  depositAmountSaving = false;
  depositAmountSuccess = '';
  depositAmountError = '';
  private _savedDepositAmount = 0;

  get isDepositDirty(): boolean { return this.depositAmount !== this._savedDepositAmount; }

  // ── Member code settings ──
  homeDeliveryPrefix = '';
  centerPrefix = '';
  memberCodeSaving = false;
  memberCodeSuccess = '';
  memberCodeError = '';
  private _savedHomeDeliveryPrefix = '';
  private _savedCenterPrefix = '';

  get isMemberCodeDirty(): boolean {
    return this.homeDeliveryPrefix !== this._savedHomeDeliveryPrefix
        || this.centerPrefix !== this._savedCenterPrefix;
  }

  // ── Date Display Format ──
  readonly dateFormatOptions = DATE_FORMAT_OPTIONS;
  selectedDateFormat = '';
  dateFormatSaving = false;
  dateFormatSuccess = '';
  dateFormatError = '';
  private _savedDateFormat = '';

  get isDateFormatDirty(): boolean { return this.selectedDateFormat !== this._savedDateFormat; }

  // ── Access code ──
  currentClientCode = '';
  newClientCode = '';
  accessCodeSaving = false;
  accessCodeSuccess = '';
  accessCodeError = '';

  get isAccessCodeDirty(): boolean {
    return !!this.newClientCode.trim() && this.newClientCode.trim() !== this.currentClientCode;
  }

  private _savedSnapshot = '';

  get isDirty(): boolean {
    return this._savedSnapshot !== this._snapshot();
  }

  private _snapshot(): string {
    return JSON.stringify(this.plans.map(p => ({
      months: Number(p.months), label: p.label, subscriptionType: p.subscriptionType, charges: Number(p.charges)
    })));
  }

  constructor(
    private api: ApiService,
    readonly auth: AuthService,
    private pageTitle: PageTitleService,
    private dateFormatSvc: DateFormatService,
  ) {}

  ngOnInit(): void {
    this.pageTitle.set('Settings', 'System configuration');
    this.loadPlans();
    this.loadGeneralSettings();
    this.loadNameSource();
    this.loadMemberCodeSettings();
    this.loadClientCode();
    this.loadDateFormat();
  }

  loadNameSource(): void {
    this.api.getLabelNameSource().subscribe({
      next: (data) => {
        this.labelNameSource     = (data?.source as any) || 'center';
        this.labelNameCustomText = data?.customText || '';
      },
      error: () => {},
    });
  }

  saveNameSource(): void {
    if (this.labelNameSource === 'custom' && !this.labelNameCustomText.trim()) {
      this.nameSourceError = 'Please enter the custom text.';
      return;
    }
    this.isSavingNameSource = true;
    this.nameSourceError    = '';
    this.nameSourceSuccess  = '';
    this.api.saveLabelNameSource(this.labelNameSource, this.labelNameCustomText).subscribe({
      next: () => {
        this.isSavingNameSource = false;
        this.nameSourceSuccess  = 'Setting saved.';
        setTimeout(() => this.nameSourceSuccess = '', 3000);
      },
      error: (err: any) => {
        this.isSavingNameSource = false;
        this.nameSourceError = err?.message || err?.error?.message || 'Failed to save setting.';
      },
    });
  }

  loadGeneralSettings(): void {
    this.api.getGeneralSettings().subscribe({
      next: (data) => {
        this.depositAmount = data?.depositAmount ?? 0;
        this._savedDepositAmount = this.depositAmount;
      },
      error: () => {},
    });
  }

  saveDepositAmount(): void {
    this.depositAmountError = '';
    this.depositAmountSuccess = '';
    if (this.depositAmount < 0) { this.depositAmountError = 'Deposit amount cannot be negative.'; return; }
    this.depositAmountSaving = true;
    this.api.updateGeneralSettings(this.depositAmount).subscribe({
      next: (data) => {
        this._savedDepositAmount = data?.depositAmount ?? this.depositAmount;
        this.depositAmount = this._savedDepositAmount;
        this.depositAmountSuccess = 'Deposit amount saved.';
        this.depositAmountSaving = false;
        setTimeout(() => this.depositAmountSuccess = '', 3000);
      },
      error: (err) => {
        this.depositAmountError = err?.message || 'Failed to save deposit amount.';
        this.depositAmountSaving = false;
      },
    });
  }

  loadMemberCodeSettings(): void {
    this.api.getMemberCodeSettings().subscribe({
      next: (data) => {
        this.homeDeliveryPrefix       = data?.homeDeliveryPrefix || '';
        this.centerPrefix             = data?.centerPrefix || '';
        this._savedHomeDeliveryPrefix = this.homeDeliveryPrefix;
        this._savedCenterPrefix       = this.centerPrefix;
      },
      error: () => {},
    });
  }

  saveMemberCodeSettings(): void {
    this.memberCodeError   = '';
    this.memberCodeSuccess = '';
    const hdp = this.homeDeliveryPrefix.trim().toUpperCase();
    const cp  = this.centerPrefix.trim().toUpperCase();
    if (hdp && !/^[A-Z0-9]+$/.test(hdp)) { this.memberCodeError = 'Home Delivery prefix may only contain letters and digits.'; return; }
    if (cp  && !/^[A-Z0-9]+$/.test(cp))  { this.memberCodeError = 'Center prefix may only contain letters and digits.'; return; }
    this.memberCodeSaving = true;
    this.api.updateMemberCodeSettings(hdp, cp).subscribe({
      next: (data) => {
        this.homeDeliveryPrefix       = data?.homeDeliveryPrefix || '';
        this.centerPrefix             = data?.centerPrefix || '';
        this._savedHomeDeliveryPrefix = this.homeDeliveryPrefix;
        this._savedCenterPrefix       = this.centerPrefix;
        this.memberCodeSuccess = 'Member code settings saved.';
        this.memberCodeSaving  = false;
        setTimeout(() => this.memberCodeSuccess = '', 3000);
      },
      error: (err: any) => {
        this.memberCodeError  = err?.message || err?.error?.message || 'Failed to save settings.';
        this.memberCodeSaving = false;
      },
    });
  }

  loadClientCode(): void {
    if (this.auth.getCurrentUser()?.role !== 'admin') return;
    this.api.getClientCode().subscribe({
      next: (data) => {
        this.currentClientCode = data?.clientCode || '';
      },
      error: () => {},
    });
  }

  loadDateFormat(): void {
    this.api.getDateFormat().subscribe({
      next: (data) => {
        this.selectedDateFormat  = data?.dateFormat || 'dd/MM/yyyy';
        this._savedDateFormat    = this.selectedDateFormat;
        this.dateFormatSvc.setFormat(this.selectedDateFormat);
      },
      error: () => {
        this.selectedDateFormat = this.dateFormatSvc.current;
        this._savedDateFormat   = this.selectedDateFormat;
      },
    });
  }

  saveDateFormatSetting(): void {
    this.dateFormatError   = '';
    this.dateFormatSuccess = '';
    this.dateFormatSaving  = true;
    this.api.saveDateFormat(this.selectedDateFormat).subscribe({
      next: (data) => {
        this._savedDateFormat = data?.dateFormat || this.selectedDateFormat;
        this.selectedDateFormat = this._savedDateFormat;
        this.dateFormatSvc.setFormat(this.selectedDateFormat);
        this.dateFormatSaving  = false;
        this.dateFormatSuccess = 'Date format saved.';
        setTimeout(() => this.dateFormatSuccess = '', 3000);
      },
      error: (err: any) => {
        this.dateFormatError  = err?.message || 'Failed to save date format.';
        this.dateFormatSaving = false;
      },
    });
  }

  saveAccessCode(): void {
    this.accessCodeError   = '';
    this.accessCodeSuccess = '';
    const code = this.newClientCode.trim();
    if (!code) { this.accessCodeError = 'Access code is required.'; return; }
    if (code.length > 10) { this.accessCodeError = 'Maximum 10 characters allowed.'; return; }
    if (!/^[A-Za-z0-9]+$/.test(code)) { this.accessCodeError = 'Only letters and digits are allowed (no spaces or special characters).'; return; }
    this.accessCodeSaving = true;
    this.api.updateClientCode(code).subscribe({
      next: (data) => {
        this.currentClientCode = data?.clientCode || code;
        this.newClientCode     = '';
        this.accessCodeSuccess = `Access code updated to "${this.currentClientCode}". All users must use this new code at their next login.`;
        this.accessCodeSaving  = false;
        setTimeout(() => this.accessCodeSuccess = '', 8000);
      },
      error: (err: any) => {
        this.accessCodeError  = err?.message || 'Failed to update access code.';
        this.accessCodeSaving = false;
      },
    });
  }

  loadPlans(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.api.getSubscriptionPlans().subscribe({
      next: (data) => {
        this.plans = (data || []).map((p: any) => ({
          months: p.months,
          label: p.label || '',
          subscriptionType: p.subscriptionType || '',
          charges: p.charges,
        }));
        this._savedSnapshot = this._snapshot();
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = err?.message || 'Failed to load plans.';
        this.isLoading = false;
      }
    });
  }

  addPlan(): void {
    this.plans = [...this.plans, { months: null, label: '', subscriptionType: '', charges: null }];
  }

  removePlan(index: number): void {
    this.plans = this.plans.filter((_, i) => i !== index);
  }

  onMonthsChange(plan: PlanRow): void {
    const m = Number(plan.months);
    if (!m || m < 1) return;
    if (!plan.label) plan.label = `${m} Month${m !== 1 ? 's' : ''}`;
    if (!plan.subscriptionType) plan.subscriptionType = TYPE_MAP[m] || `${m} Month${m !== 1 ? 's' : ''}`;
  }

  saveChanges(): void {
    this.errorMessage = '';
    this.successMessage = '';

    for (const p of this.plans) {
      const m = Number(p.months);
      const c = Number(p.charges);
      if (!m || m < 1) { this.errorMessage = 'All plans must have a valid months value.'; return; }
      if (!c || c <= 0) { this.errorMessage = `Plan "${p.label || m + ' months'}" must have valid charges.`; return; }
    }

    this.isSaving = true;
    this.api.updateSubscriptionPlans(
      this.plans.map(p => ({ months: p.months, label: p.label, subscriptionType: p.subscriptionType, charges: p.charges }))
    ).subscribe({
      next: (updated) => {
        this.plans = (updated || []).map((p: any) => ({
          months: p.months, label: p.label || '', subscriptionType: p.subscriptionType || '', charges: p.charges
        }));
        this._savedSnapshot = this._snapshot();
        this.successMessage = 'Subscription plans saved successfully.';
        this.isSaving = false;
        setTimeout(() => this.successMessage = '', 4000);
      },
      error: (err) => {
        this.errorMessage = err?.message || 'Failed to save plans.';
        this.isSaving = false;
      }
    });
  }
}
